import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { PhotoIcon, TrashIcon } from '@heroicons/react/24/outline';
import client from '../../../lib/feathers';

import putToStorage from '../../../lib/putToStorage';
const DEFAULT_OPT_IN_COPY = 'By participating in this passport, you consent to share your contact information (name, email, check-in date) with the venues you visit during the passport period.';

export default function IndustryPassportEdit() {
  const { pid, lid } = useParams();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const listing = useSelector((s) => s.listings.selected);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [coverImageUploading, setCoverImageUploading] = useState(false);
  const coverImageInputRef = useRef(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startDate: '',
    endDate: '',
    completionThreshold: '5',
    status: 'draft',
    optInCopy: '',
    askMembership: false,
    coverImageAttachmentId: null,
  });
  const [coverImage, setCoverImage] = useState(null);
  const [thresholdError, setThresholdError] = useState('');

  const loadPassport = async () => {
    try {
      setLoading(true);
      const service = client.service('industry/passport');
      const result = await service.get(pid);

      // Only organizers can edit
      if (!result.isOrganizer) {
        setError('You do not have permission to edit this passport');
        return;
      }

      setFormData({
        title: result.title || '',
        description: result.description || '',
        startDate: result.startDate?.split('T')[0] || '',
        endDate: result.endDate?.split('T')[0] || '',
        completionThreshold: result.completionThreshold?.toString() || '5',
        status: result.status || 'draft',
        optInCopy: result.optInCopy || '',
        askMembership: result.askMembership || false,
        coverImageAttachmentId: result.coverImageAttachmentId,
      });
      setCoverImage(result.coverImage);
    } catch (e) {
      console.error('Error loading passport:', e);
      setError('Failed to load passport');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPassport();
  }, [pid]);

  // Validate completion threshold format
  const validateThreshold = (value) => {
    const cleaned = value.replace(/\s/g, '');
    if (!cleaned) {
      setThresholdError('Completion threshold is required');
      return false;
    }
    if (!/^(\d+)(,\d+)*$/.test(cleaned)) {
      setThresholdError('Enter numbers separated by commas (e.g., "3" or "3,5,7")');
      return false;
    }
    const numbers = cleaned.split(',').map(n => parseInt(n, 10));
    if (numbers.some(n => n < 1)) {
      setThresholdError('All values must be at least 1');
      return false;
    }
    setThresholdError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateThreshold(formData.completionThreshold)) {
      return;
    }
    try {
      setSaving(true);
      const service = client.service('industry/passport');
      await service.patch(pid, formData);
      navigate(`/industry/listings/${lid}/passports/${pid}`);
    } catch (err) {
      console.error('Error saving passport:', err);
      alert(err.message || 'Failed to save passport');
    } finally {
      setSaving(false);
    }
  };

  const handleCoverImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setCoverImageUploading(true);
    try {
      const uploadService = client.service(`${user.role}/passport-cover-upload`);

      // Get signed URL for upload
      const signedUrlData = await uploadService.create({
        passportId: pid,
        'file-type': file.type,
      });

      // Upload to S3
      await putToStorage(signedUrlData.signedRequest, file);

      // Create attachment record and update passport
      const result = await uploadService.patch(pid, {
        path: signedUrlData.path,
        mime: file.type,
      });

      setFormData({ ...formData, coverImageAttachmentId: result.coverImageAttachmentId });
      setCoverImage(result.coverImage);
    } catch (err) {
      console.error('Error uploading cover image:', err);
      alert(err.message || 'Failed to upload cover image');
    } finally {
      setCoverImageUploading(false);
    }
  };

  const handleRemoveCoverImage = () => {
    if (!window.confirm('Are you sure you want to remove the cover image?')) return;
    setFormData({ ...formData, coverImageAttachmentId: null });
    setCoverImage(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-icon" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <p className="text-red-800">{error}</p>
        <Link to={`/industry/listings/${lid}/passports`} className="mt-2 text-icon hover:underline">
          Back to Passports
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="pb-5 border-b border-gray-200">
        <Link to={`/industry/listings/${lid}/passports/${pid}`} className="text-sm text-gray-500 hover:text-gray-700">
          &larr; Back to Passport
        </Link>
        <h1 className="mt-2 text-2xl font-bold text-gray-900">Edit Passport</h1>
        {listing && (
          <p className="mt-1 text-sm text-gray-500">
            Editing from: {listing.name}
          </p>
        )}
      </div>

      <form onSubmit={handleSubmit} className="mt-6 space-y-6">
        {/* Basic Info */}
        <div className="bg-white shadow sm:rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
          <div className="space-y-4">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                Title *
              </label>
              <input
                type="text"
                id="title"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-icon focus:border-icon sm:text-sm"
              />
            </div>

            {/* Cover Image */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cover Image
              </label>
              <div className="flex items-center gap-4">
                {coverImage ? (
                  <div className="relative">
                    <img
                      src={`${process.env.REACT_APP_ATTACHMENT_URL}/${coverImage.path}`}
                      alt="Passport cover"
                      className="h-32 w-48 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveCoverImage}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div
                    className="h-32 w-48 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-gray-400"
                    onClick={() => coverImageInputRef.current?.click()}
                  >
                    {coverImageUploading ? (
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-icon" />
                    ) : (
                      <>
                        <PhotoIcon className="h-10 w-10 text-gray-400" />
                        <span className="mt-1 text-sm text-gray-500">Upload image</span>
                      </>
                    )}
                  </div>
                )}
                <input
                  type="file"
                  ref={coverImageInputRef}
                  onChange={handleCoverImageUpload}
                  accept="image/*"
                  className="hidden"
                />
                {coverImage && (
                  <button
                    type="button"
                    onClick={() => coverImageInputRef.current?.click()}
                    disabled={coverImageUploading}
                    className="text-sm text-icon hover:text-red-700"
                  >
                    {coverImageUploading ? 'Uploading...' : 'Change image'}
                  </button>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                id="description"
                rows={4}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-icon focus:border-icon sm:text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">Supports Markdown formatting</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="startDate" className="block text-sm font-medium text-gray-700">
                  Start Date *
                </label>
                <input
                  type="date"
                  id="startDate"
                  required
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-icon focus:border-icon sm:text-sm"
                />
              </div>

              <div>
                <label htmlFor="endDate" className="block text-sm font-medium text-gray-700">
                  End Date *
                </label>
                <input
                  type="date"
                  id="endDate"
                  required
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-icon focus:border-icon sm:text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="completionThreshold" className="block text-sm font-medium text-gray-700">
                  Completion Milestones *
                </label>
                <input
                  type="text"
                  id="completionThreshold"
                  required
                  value={formData.completionThreshold}
                  onChange={(e) => {
                    setFormData({ ...formData, completionThreshold: e.target.value });
                    validateThreshold(e.target.value);
                  }}
                  placeholder="e.g., 3 or 3,5,7"
                  className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-icon focus:border-icon sm:text-sm ${thresholdError ? 'border-red-300' : ''}`}
                />
                {thresholdError ? (
                  <p className="mt-1 text-xs text-red-600">{thresholdError}</p>
                ) : (
                  <p className="mt-1 text-xs text-gray-500">
                    Enter one number or multiple (e.g., &quot;3,5,7&quot;). First number = completion, others are bonus milestones.
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                  Status
                </label>
                <select
                  id="status"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-icon focus:border-icon sm:text-sm"
                >
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="ended">Ended</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Opt-In Settings */}
        <div className="bg-white shadow sm:rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Opt-In Settings</h3>
          <div className="space-y-4">
            <div>
              <label htmlFor="optInCopy" className="block text-sm font-medium text-gray-700">
                Opt-In Message
              </label>
              <textarea
                id="optInCopy"
                rows={3}
                value={formData.optInCopy}
                onChange={(e) => setFormData({ ...formData, optInCopy: e.target.value })}
                placeholder={DEFAULT_OPT_IN_COPY}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-icon focus:border-icon sm:text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">
                Leave blank to use the default opt-in message
              </p>
            </div>

            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="askMembership"
                  type="checkbox"
                  checked={formData.askMembership}
                  onChange={(e) => setFormData({ ...formData, askMembership: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-icon focus:ring-icon"
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="askMembership" className="font-medium text-gray-700">
                  Ask about club memberships
                </label>
                <p className="text-gray-500">
                  When enabled, users will be asked which wine clubs they belong to when opting in
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate(`/industry/listings/${lid}/passports/${pid}`)}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-icon hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-icon disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
